import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { DocumentIcon, PlusIcon } from '@repo/ui/icons'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { getEndOfJourneyTemplates } from '../../../api/end-of-journey-templates/get-end-of-journey-templates'
import { DynamicDataTableSearchInput } from '../../../components/elements/dynamic-table'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import EndOfStayTemplateCardItem from '../../../components/pages/end-of-stay-templates/end-of-stay-template-card-item'
import { Paths } from '../../../lib/constants/path'
import { useUrl } from '../../../lib/hooks/use-url'
import useMemorizedDebounce from '../../../lib/hooks/useMemorizedDebounce'

const EndOfStayTemplateListPage = () => {
  const { navigate } = useNavigator()
  const { readSearchParam, setSearchParams } = useUrl()

  const [searchInput, setSearchInput] = useState(() => readSearchParam<string>('search') || '')
  const [search, setSearch] = useState(() => readSearchParam<string>('search') || '')
  const debouncedSearch = useMemorizedDebounce(searchInput, 500)

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['end-of-journey-templates-list', search],
    queryFn: () => getEndOfJourneyTemplates({ search }),
    placeholderData: keepPreviousData
  })

  // Update search state and URL when debounced search changes
  useEffect(() => {
    setSearch(debouncedSearch)

    // Update URL with search parameter
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params)
      if (debouncedSearch) newParams.set('search', debouncedSearch)
      else newParams.delete('search')
      return newParams.toString()
    })
  }, [debouncedSearch, setSearchParams])

  // Only show full loading screen on initial load (no cached data)
  if (isLoading) return <LoadingScreen />

  return (
    <div>
      <UserNavBar title="Appréciations de fin de séjour" icon={DocumentIcon} />
      <div className="h-4" />
      <Box>
        <div className="flex justify-end items-center">
          <DynamicDataTableSearchInput
            headerType="full"
            value={searchInput}
            onChange={(value) => setSearchInput(value)}
          />
        </div>
        <div className="h-4" />

        <Button
          icon={PlusIcon}
          label="Créer un template"
          size="medium"
          onClick={() => {
            navigate(Paths.SURVEY_END_OF_STAY_TEMPLATE_CREATE_PAGE)
          }}
        />
        <div className="h-4" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {data?.data.map((template) => (
            <EndOfStayTemplateCardItem key={template.id} template={template} onRefresh={refetch} />
          ))}
        </div>
      </Box>
    </div>
  )
}

export default EndOfStayTemplateListPage
